import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid2';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { SessionContext } from '../../Contexts/SessionContext';
import ExportViewModel from '../ViewModels/ExportViewModel';
import ModeSelectionViewModel from '../ViewModels/ModeSelectionViewModel';

interface IProps {
    dialogOpen: boolean;
    handleClose: () => void;
    exportTripRate: (filters: ExportViewModel, auditcode?: string) => void;
    availableModes: ModeSelectionViewModel[];
    selectedModeId: number;
}

const sectionSx = { border: '1px solid #ccc', borderRadius: 1, p: 1, mb: 1 };

export default function ExportDialog({ dialogOpen, handleClose, exportTripRate, availableModes, selectedModeId }: IProps) {
    const [filters, setFilters] = useState(new ExportViewModel());
    const [allVeh, setAllVeh] = useState(false);
    const { saveSession } = useContext(SessionContext);

    const auditPara = 'Save this session and write a reference number on the report so this session can be reviewed later by an auditor';

    const toField = (s: string) =>
        s.split(/[\s\/]+/)
            .map((w, i) =>
                i ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w.toLowerCase()
            )
            .join('');

    const handleCheck = (e: ChangeEvent<HTMLInputElement>) =>
        setFilters(f => ({ ...f, [e.target.name]: e.target.checked }));

    const handleAllVeh = (e: ChangeEvent<HTMLInputElement>) => {
        const chk = e.target.checked;
        setAllVeh(chk);
        setFilters(f => {
            const up = { ...f };
            availableModes
                .filter(m => m.isSelectable && m.modeId !== selectedModeId)
                .forEach(m => {
                    const key = toField(m.modeName);
                    (up as any)[key] = chk;
                });

            const selectedMode = availableModes.find(m => m.modeId === selectedModeId);
            if (selectedMode) {
                const selectedKey = toField(selectedMode.modeName);
                (up as any)[selectedKey] = true;
            }
            return up;
        });
    };

    useEffect(() => {
        if (!dialogOpen) return;

        const selectedMode = availableModes.find(m => m.modeId === selectedModeId);
        if (!selectedMode) return;

        const selectedKey = toField(selectedMode.modeName);
        setFilters(f => ({ ...f, [selectedKey]: true } as ExportViewModel));
    }, [dialogOpen, availableModes, selectedModeId]);

    const handleRadio = (name: keyof ExportViewModel) => (
        e: ChangeEvent<HTMLInputElement>
    ) =>
        setFilters(f => ({
            ...f,
            [name]: (e.target as HTMLInputElement).value
        }));

    const reset = () => {
        setFilters(new ExportViewModel());
        setAllVeh(false);
        handleClose();
    };

    const submit = () => {
        if (filters.auditors) {
            saveSession(`Trip_Rate_${new Date().toISOString()}`, 'audit', data =>
                exportTripRate(filters, data)
            );
        } else exportTripRate(filters);
        reset();
    };

    const hours = Array.from({ length: 24 }, (_, i) =>
        `${String(i + 1).padStart(2, '0')}:00`
    );

    // Ensure null/undefined is treated as not selected
    const startPeriod: number | undefined =
        filters.userDefinedPeriodStart !== null &&
            filters.userDefinedPeriodStart !== undefined
            ? filters.userDefinedPeriodStart
            : undefined;
    const endPeriod: number | undefined =
        filters.userDefinedPeriodEnd !== null &&
            filters.userDefinedPeriodEnd !== undefined
            ? filters.userDefinedPeriodEnd
            : undefined;

    // Validation for user defined period
    const isUserPeriodInvalid =
        filters.userDefinedPeriod &&
        (
            startPeriod === undefined ||
            endPeriod === undefined ||
            startPeriod > endPeriod
        );


    const handleText = (e: ChangeEvent<HTMLInputElement>) => {
        setFilters(f => ({
            ...f,
            userComments: (e.target as HTMLInputElement).value
        }))
    }

    return (
        <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="xl">
            <DialogTitle>Trip Rate Export</DialogTitle>
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid size={4}>
                        <Grid size={12}>
                            <Box sx={sectionSx}>
                                <Typography variant="subtitle2">Report Options</Typography>
                                <TextField
                                    label="Report Name"
                                    value={filters.reportTitle}
                                    onChange={e =>
                                        setFilters(f => ({ ...f, reportTitle: e.target.value }))
                                    }
                                    fullWidth
                                    size="small"
                                    margin="dense"
                                />
                                <RadioGroup
                                    row
                                    name="exportType"
                                    value={filters.exportType}
                                    onChange={handleRadio('exportType')}
                                >
                                    <FormControlLabel
                                        value="pdf"
                                        control={<Radio size="small" />}
                                        label="PDF"
                                    />
                                    <FormControlLabel
                                        value="csv"
                                        control={<Radio size="small" />}
                                        label="CSV"
                                    />
                                </RadioGroup>
                            </Box>
                        </Grid>

                        {filters.exportType === 'csv' && (
                            <>
                                <Grid size={12}>
                                    <Box sx={sectionSx}>
                                        <Typography variant="subtitle2">CSV Data</Typography>
                                        <RadioGroup
                                            row
                                            name="csvDataToCopy"
                                            value={filters.csvDataToCopy}
                                            onChange={handleRadio('csvDataToCopy')}
                                        >
                                            <FormControlLabel
                                                value="tripRateOnly"
                                                control={<Radio size="small" />}
                                                label="Table Only"
                                            />
                                            <FormControlLabel
                                                value="tripRateTableAndSelections"
                                                control={<Radio size="small" />}
                                                label="Table + Selections"
                                            />
                                        </RadioGroup>
                                    </Box>
                                </Grid>
                                {filters.csvDataToCopy === 'tripRateTableAndSelections' && (
                                    <Grid size={12}>
                                        <Box sx={sectionSx}>
                                            <FormGroup>
                                                {[
                                                    {
                                                        name: 'includeEnhancedSiteList',
                                                        label: 'Enhanced Site List'
                                                    },
                                                    {
                                                        name: 'includeFilteringSummary',
                                                        label: 'Filtering Summary'
                                                    }
                                                ].map(opt => (
                                                    <FormControlLabel
                                                        key={opt.name}
                                                        control={
                                                            <Checkbox
                                                                name={opt.name}
                                                                checked={(filters as any)[opt.name]}
                                                                onChange={handleCheck}
                                                                size="small"
                                                            />
                                                        }
                                                        label={opt.label}
                                                        sx={{ mb: 0.5 }}
                                                    />
                                                ))}
                                            </FormGroup>
                                        </Box>
                                    </Grid>
                                )}
                            </>
                        )}

                        {filters.exportType === 'pdf' && (
                            <Grid size={12}>
                                <Box sx={sectionSx}>
                                    <Typography variant="subtitle2">
                                        Selection Parameters
                                    </Typography>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="selectionParameters"
                                                    checked={filters.selectionParameters}
                                                    onChange={handleCheck}
                                                    size="small"
                                                />
                                            }
                                            label="Details of Selection Parameters"
                                            sx={{ mb: 0.5 }}
                                        />
                                        <Box sx={{ pl: 2 }}>
                                            {[
                                                'regionsAndAreas',
                                                'mainParameters',
                                                'secondaryFiltering',
                                                'includeFilteringSummary',
                                                'includeEnhancedSiteList'
                                            ].map(name => (
                                                <FormControlLabel
                                                    key={name}
                                                    control={
                                                        <Checkbox
                                                            name={name}
                                                            checked={(filters as any)[name]}
                                                            onChange={handleCheck}
                                                            disabled={!filters.selectionParameters}
                                                            size="small"
                                                        />
                                                    }
                                                    label={{
                                                        regionsAndAreas: 'Regions & Areas',
                                                        mainParameters: 'Main Parameter Ranges',
                                                        secondaryFiltering: 'Secondary Filtering',
                                                        includeFilteringSummary: 'Include Filtering Summary',
                                                        includeEnhancedSiteList: 'Include Enhanced Site List'
                                                    }[name]}
                                                    sx={{ mb: 0.5 }}
                                                />
                                            ))}
                                        </Box>
                                    </FormGroup>
                                </Box>
                            </Grid>
                        )}
                    </Grid>

                    <Grid size={4}>
                        {filters.exportType === 'pdf' && (
                            <Grid size={12}>
                                <Box sx={sectionSx}>
                                    <Typography variant="subtitle2">For Auditors</Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        {auditPara}
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="auditors"
                                                checked={filters.auditors}
                                                onChange={handleCheck}
                                                size="small"
                                            />
                                        }
                                        label="Save session & add reference"
                                    />
                                </Box>
                            </Grid>
                        )}

                        <Grid size={12}>
                            <Box sx={sectionSx}>
                                <Typography variant="subtitle2">
                                    Additional Trip Rates
                                </Typography>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox checked={allVeh} onChange={handleAllVeh} size="small" />
                                        }
                                        label="Select All"
                                        sx={{ mb: 0.5 }}
                                    />
                                    <Box sx={{ pl: 2 }}>
                                        {availableModes
                                            .filter(m => m.isSelectable)
                                            .map(m => {
                                                const key = toField(m.modeName);
                                                const isSelectedMode = m.modeId === selectedModeId;
                                                return (
                                                    <FormControlLabel
                                                        key={key}
                                                        control={
                                                            <Checkbox
                                                                name={key}
                                                                checked={isSelectedMode ? true : (filters as any)[key] || false}
                                                                onChange={isSelectedMode ? undefined : handleCheck}
                                                                size="small"
                                                                disabled={isSelectedMode}
                                                            />
                                                        }
                                                        label={m.modeName}
                                                        sx={{ mb: 0.5 }}
                                                    />
                                                );
                                            })}
                                    </Box>
                                </FormGroup>
                            </Box>
                        </Grid>
                    </Grid>

                    <Grid size={4}>
                        <Grid size={12}>
                            <Box sx={sectionSx}>
                                <Typography variant="subtitle2">
                                    Key Period Trip Rates
                                </Typography>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="keyPeriod"
                                                checked={filters.keyPeriod}
                                                onChange={handleCheck}
                                                size="small"
                                            />
                                        }
                                        label="Include Key Periods"
                                        sx={{ mb: 0.5 }}
                                    />
                                    {filters.keyPeriod && (
                                        <Box sx={{ pl: 2 }}>
                                            <Typography variant="body2">
                                                Period 1: 08:00–09:00
                                            </Typography>
                                            <Typography variant="body2">
                                                Period 2: 17:00–18:00
                                            </Typography>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name="userDefinedPeriod"
                                                        checked={filters.userDefinedPeriod}
                                                        onChange={handleCheck}
                                                        size="small"
                                                    />
                                                }
                                                label="Custom Period"
                                                sx={{ my: 0.5 }}
                                            />
                                            {filters.userDefinedPeriod && (
                                                <>
                                                    <TextField
                                                        select
                                                        label="Start"
                                                        value={startPeriod ?? ''}
                                                        onChange={e =>
                                                            setFilters(f => ({
                                                                ...f,
                                                                userDefinedPeriodStart: e.target.value === '' ? null : +e.target.value
                                                            }))
                                                        }
                                                        size="small"
                                                        margin="dense"
                                                        fullWidth
                                                        error={isUserPeriodInvalid}
                                                    >
                                                        <MenuItem value="">
                                                            (Select start)
                                                        </MenuItem>
                                                        {hours.map((h, i) => (
                                                            <MenuItem
                                                                key={h}
                                                                value={i + 1}
                                                                disabled={
                                                                    endPeriod !== undefined &&
                                                                    i + 1 > endPeriod
                                                                }
                                                            >
                                                                {h === '00:00' ? '24:00' : h}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                    <TextField
                                                        select
                                                        label="End"
                                                        value={endPeriod ?? ''}
                                                        onChange={e =>
                                                            setFilters(f => ({
                                                                ...f,
                                                                userDefinedPeriodEnd: e.target.value === '' ? null : +e.target.value
                                                            }))
                                                        }
                                                        size="small"
                                                        margin="dense"
                                                        fullWidth
                                                        error={isUserPeriodInvalid}
                                                    >
                                                        <MenuItem value="">
                                                            (Select end)
                                                        </MenuItem>
                                                        {hours.map((h, i) => (
                                                            <MenuItem
                                                                key={h}
                                                                value={i + 1}
                                                                disabled={
                                                                    startPeriod !== undefined &&
                                                                    i + 1 < startPeriod
                                                                }
                                                            >
                                                                {h === '00:00' ? '24:00' : h}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                    {isUserPeriodInvalid && (
                                                        <Typography color="error" variant="caption">
                                                            Start period must not be after end period, and both must be selected.
                                                        </Typography>
                                                    )}
                                                </>
                                            )}
                                        </Box>
                                    )}
                                </FormGroup>
                            </Box>
                        </Grid>

                        {filters.exportType === 'pdf' && (
                            <>
                                <Grid size={12}>
                                    <Box sx={sectionSx}>
                                        <Typography variant="subtitle2">Trip Rate Graph</Typography>
                                        <FormGroup>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name="tripRateGraph"
                                                        checked={filters.tripRateGraph}
                                                        onChange={handleCheck}
                                                        size="small"
                                                    />
                                                }
                                                label="Include Graph"
                                                sx={{ mb: 0.5 }}
                                            />
                                            <Box sx={{ pl: 2 }}>
                                                {['arrivals', 'departures', 'totals'].map(name => (
                                                    <FormControlLabel
                                                        key={name}
                                                        control={
                                                            <Checkbox
                                                                name={name}
                                                                checked={(filters as any)[name]}
                                                                onChange={handleCheck}
                                                                disabled={!filters.tripRateGraph}
                                                                size="small"
                                                            />
                                                        }
                                                        label={
                                                            name.charAt(0).toUpperCase() + name.slice(1)
                                                        }
                                                        sx={{ mb: 0.5 }}
                                                    />
                                                ))}
                                            </Box>
                                        </FormGroup>
                                    </Box>
                                </Grid>
                                <Grid size={12}>
                                    <Box sx={sectionSx}>
                                        <Typography variant="subtitle2">Included Sites</Typography>
                                        <FormGroup>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name="includedSiteList"
                                                        checked={filters.includedSiteList}
                                                        onChange={handleCheck}
                                                        size="small"
                                                    />
                                                }
                                                label="Site List"
                                                sx={{ mb: 0.5 }}
                                            />
                                            <Box sx={{ pl: 2 }}>
                                                {['siteDetails', 'surveyDates'].map(name => (
                                                    <FormControlLabel
                                                        key={name}
                                                        control={
                                                            <Checkbox
                                                                name={name}
                                                                checked={(filters as any)[name]}
                                                                onChange={handleCheck}
                                                                disabled={!filters.includedSiteList}
                                                                size="small"
                                                            />
                                                        }
                                                        label={{
                                                            siteDetails: 'Site Details',
                                                            surveyDates: 'Survey Dates'
                                                        }[name]}
                                                        sx={{ mb: 0.5 }}
                                                    />
                                                ))}
                                            </Box>
                                        </FormGroup>
                                    </Box>
                                </Grid>

                                <Grid size={12}>
                                    <Box sx={sectionSx}>
                                        <Typography variant="subtitle2">
                                            Comments
                                        </Typography>
                                        <FormGroup>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name="addUserComments"
                                                        checked={filters.addUserComments}
                                                        onChange={handleCheck}
                                                        size="small"
                                                    />
                                                }
                                                label="Add User Comments to Report"
                                                sx={{ mb: 0.5 }}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name="explanatoryCommentsIncluded"
                                                        checked={filters.explanatoryCommentsIncluded}
                                                        onChange={handleCheck}
                                                        size="small"
                                                    />
                                                }
                                                label=" Include explanatory commentary in the report"
                                                sx={{ mb: 0.5 }}
                                            />
                                        </FormGroup>
                                        {filters.addUserComments &&
                                            <TextField label={"User Comments"} fullWidth onChange={handleText} />

                                        }
                                    </Box>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={submit} size="small" disabled={isUserPeriodInvalid}>
                    OK
                </Button>
                <Button onClick={reset} size="small">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
